import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CircularProgress, Alert, Snackbar, Chip, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftSelect from 'components/SoftSelect';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getAllSalesLeads, deleteSalesLead, deleteSalesLeads, markLeadsPrinted, markLeadsExported } from 'services/salesLeadService';
import { landingUrlFor } from '../fields';
import { generateAvery8162Pdf, hasAddress } from '../labels/avery8162';
import { exportLeadsToCsv } from '../export-leads';
import ImportLeadsDialog from '../import-leads-dialog';

const EMPTY_FILTERS = { search: '', leadType: '', status: '', notPrinted: false, notExported: false };

const TYPE_OPTIONS = [
  { value: '', label: 'Type: All' },
  { value: 'dealer', label: 'Dealer' },
  { value: 'rep_card', label: 'Rep card' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Status: All' },
  { value: 'new', label: 'New' },
  { value: 'converted', label: 'Converted' },
];

const SEARCH_FIELDS = ['contactName', 'businessName', 'salesRep', 'businessEmail', 'businessPhone', 'city', 'state'];

const SORT_COLUMNS = [
  { key: 'leadType', label: 'Type' },
  { key: 'contactName', label: 'Contact' },
  { key: 'businessName', label: 'Dealership' },
  { key: 'salesRep', label: 'Sales Rep' },
  { key: 'trialDays', label: 'Trial' },
  { key: 'discountPercent', label: 'Discount' },
  { key: 'clickCount', label: 'Clicks' },
  { key: 'printedAt', label: 'Printed' },
  { key: 'exportedAt', label: 'Exported' },
  { key: 'status', label: 'Status' },
];

const matchesFilters = (lead, f) => {
  if (f.notPrinted && lead.printedAt) return false;
  if (f.notExported && lead.exportedAt) return false;
  if (f.leadType && lead.leadType !== f.leadType) return false;
  if (f.status) {
    // Status only applies to dealer leads; rep cards track signups/conversions instead.
    if (lead.leadType === 'rep_card') return false;
    if ((lead.status === 'converted' ? 'converted' : 'new') !== f.status) return false;
  }
  const q = f.search.trim().toLowerCase();
  if (q && !SEARCH_FIELDS.some((k) => String(lead[k] || '').toLowerCase().includes(q))) return false;
  return true;
};

// Blank values (shown as "—") come back as null so they can always sort last.
const sortValue = (lead, key) => {
  let v;
  if (key === 'status') v = lead.leadType === 'rep_card' ? (lead.conversionCount || 0) : (lead.status === 'converted' ? 1 : 0);
  else if (key === 'leadType') v = lead.leadType === 'rep_card' ? 'Rep card' : 'Dealer';
  else v = lead[key];
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'string' ? v.toLowerCase() : v;
};

const sortLeads = (rows, sort) => {
  if (!sort) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
    return (av - bv) * dir;
  });
};

function SalesLeadList() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState({ open: false, lead: null });
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [printPrompt, setPrintPrompt] = useState({ open: false, leadIds: [] });
  const [bulkConfirm, setBulkConfirm] = useState(false);
  // "Not printed yet" / "Not exported yet" filters, so an operator can quickly
  // select-all + Print Labels / Export CSV on just the leads still owed one.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // null keeps the server order; otherwise { key, dir: 'asc' | 'desc' }.
  const [sort, setSort] = useState(null);

  const toggleFilter = (key) => setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const filtersActive = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  // Click cycles asc -> desc -> unsorted.
  const onSort = (key) => setSort((prev) => {
    if (!prev || prev.key !== key) return { key, dir: 'asc' };
    return prev.dir === 'asc' ? { key, dir: 'desc' } : null;
  });

  const visibleLeads = useMemo(
    () => sortLeads(leads.filter((l) => matchesFilters(l, filters)), sort),
    [leads, filters, sort]
  );
  const visibleSelectedCount = visibleLeads.filter((l) => selected.has(l.leadId)).length;

  // Selection is general (any visible lead can be checked). Label printing only
  // applies to the checked leads that actually have a mailing address.
  const printableSelected = visibleLeads.filter((l) => selected.has(l.leadId) && hasAddress(l));

  const toggleOne = (leadId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const allVisibleSelected = visibleLeads.length > 0 && visibleLeads.every((l) => prev.has(l.leadId));
      const next = new Set(prev);
      visibleLeads.forEach((l) => (allVisibleSelected ? next.delete(l.leadId) : next.add(l.leadId)));
      return next;
    });
  };

  const onPrintLabels = async () => {
    try {
      await generateAvery8162Pdf(printableSelected);
      setSnackbar({ open: true, message: `Generated ${printableSelected.length} labels`, severity: 'success' });
      setSelected(new Set());
      // After the PDF downloads, ask whether the batch actually printed so we
      // can stamp a "printed on" date on those leads.
      setPrintPrompt({ open: true, leadIds: printableSelected.map((l) => l.leadId) });
    } catch (err) {
      console.error('Error generating labels:', err);
      setSnackbar({ open: true, message: 'Failed to generate labels', severity: 'error' });
    }
  };

  const onExportCsv = async () => {
    const toExport = selected.size > 0 ? leads.filter((l) => selected.has(l.leadId)) : visibleLeads;
    exportLeadsToCsv(toExport);
    setSnackbar({ open: true, message: `Exported ${toExport.length} lead${toExport.length === 1 ? '' : 's'}`, severity: 'success' });
    try {
      await markLeadsExported(toExport.map((l) => l.leadId), Math.floor(Date.now() / 1000));
      await load();
    } catch (err) {
      console.error('Error marking leads exported:', err);
    }
  };

  const onBulkDelete = async () => {
    const leadIds = Array.from(selected);
    setBulkConfirm(false);
    try {
      await deleteSalesLeads(leadIds);
      setSnackbar({ open: true, message: `Deleted ${leadIds.length} leads`, severity: 'success' });
      setSelected(new Set());
      await load();
    } catch (err) {
      console.error('Error deleting leads:', err);
      setSnackbar({ open: true, message: 'Failed to delete leads', severity: 'error' });
    }
  };

  const onMarkPrinted = async () => {
    const { leadIds } = printPrompt;
    setPrintPrompt({ open: false, leadIds: [] });
    try {
      await markLeadsPrinted(leadIds, Math.floor(Date.now() / 1000));
      setSnackbar({ open: true, message: `Marked ${leadIds.length} leads printed`, severity: 'success' });
      await load();
    } catch (err) {
      console.error('Error marking leads printed:', err);
      setSnackbar({ open: true, message: 'Failed to mark leads printed', severity: 'error' });
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const data = await getAllSalesLeads();
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading leads:', err);
      setSnackbar({ open: true, message: 'Failed to load leads', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyLink = (e, lead) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(landingUrlFor(lead.queryString));
    setSnackbar({ open: true, message: 'Personalized link copied', severity: 'success' });
  };

  const onDelete = async () => {
    const { lead } = confirm;
    setConfirm({ open: false, lead: null });
    try {
      await deleteSalesLead(lead.leadId);
      setSnackbar({ open: true, message: 'Lead deleted', severity: 'success' });
      await load();
    } catch (err) {
      console.error('Error deleting lead:', err);
      setSnackbar({ open: true, message: 'Failed to delete lead', severity: 'error' });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <SoftTypography variant="h6">Sales Leads</SoftTypography>
            <SoftBox display="flex" gap={1}>
              <SoftButton
                variant="gradient"
                color="dark"
                disabled={printableSelected.length === 0}
                onClick={onPrintLabels}
              >
                Print Labels ({printableSelected.length})
              </SoftButton>
              <SoftButton
                variant="outlined"
                color="error"
                disabled={selected.size === 0}
                onClick={() => setBulkConfirm(true)}
              >
                Delete Selected ({selected.size})
              </SoftButton>
              <SoftButton variant="outlined" color="info" onClick={() => setImportOpen(true)}>
                Import Leads
              </SoftButton>
              <SoftButton
                variant="outlined"
                color="dark"
                disabled={leads.length === 0}
                onClick={onExportCsv}
              >
                Export CSV{selected.size > 0 ? ` (${selected.size})` : ''}
              </SoftButton>
              <SoftButton variant="gradient" color="info" onClick={() => navigate('/sales-leads/create')}>
                Add Lead
              </SoftButton>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1} px={3} pb={2}>
            <SoftBox minWidth="280px">
              <SoftInput
                placeholder="Search name, business, rep, email, phone, city…"
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
              />
            </SoftBox>
            <SoftBox minWidth="160px">
              <SoftSelect
                //size="small"
                options={TYPE_OPTIONS}
                value={TYPE_OPTIONS.find((o) => o.value === filters.leadType)}
                onChange={(o) => setFilter('leadType', o.value)}
                isSearchable={false}
              />
            </SoftBox>
            <SoftBox minWidth="160px">
              <SoftSelect
                //size="small"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === filters.status)}
                onChange={(o) => setFilter('status', o.value)}
                isSearchable={false}
              />
            </SoftBox>
            <Chip
              //size="small"
              label="Not printed"
              color={filters.notPrinted ? 'info' : 'default'}
              variant={filters.notPrinted ? 'filled' : 'outlined'}
              onClick={() => toggleFilter('notPrinted')}
            />
            <Chip
              //size="small"
              label="Not exported"
              color={filters.notExported ? 'info' : 'default'}
              variant={filters.notExported ? 'filled' : 'outlined'}
              onClick={() => toggleFilter('notExported')}
            />
            {filtersActive && (
              <>
                <SoftTypography variant="caption" color="secondary">
                  Showing {visibleLeads.length} of {leads.length}
                </SoftTypography>
                <SoftButton size="small" variant="text" color="info" onClick={() => setFilters(EMPTY_FILTERS)}>
                  Clear filters
                </SoftButton>
              </>
            )}
          </SoftBox>
          {loading ? (
            <SoftBox p={3}><CircularProgress /></SoftBox>
          ) : leads.length === 0 ? (
            <SoftBox p={3}><Alert severity="info">No sales leads yet.</Alert></SoftBox>
          ) : visibleLeads.length === 0 ? (
            <SoftBox p={3}><Alert severity="info">No leads match the current filter.</Alert></SoftBox>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ display: 'table-header-group' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={visibleLeads.length === 0}
                        checked={visibleLeads.length > 0 && visibleSelectedCount === visibleLeads.length}
                        indeterminate={visibleSelectedCount > 0 && visibleSelectedCount < visibleLeads.length}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    {SORT_COLUMNS.map((col) => (
                      <TableCell key={col.key} sortDirection={sort?.key === col.key ? sort.dir : false}>
                        <TableSortLabel
                          active={sort?.key === col.key}
                          direction={sort?.key === col.key ? sort.dir : 'asc'}
                          onClick={() => onSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLeads.map((lead) => (
                    <TableRow
                      key={lead.leadId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/sales-leads/edit?leadId=${encodeURIComponent(lead.leadId)}`)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(lead.leadId)}
                          onChange={() => toggleOne(lead.leadId)}
                        />
                      </TableCell>
                      <TableCell>{lead.leadType === 'rep_card' ? 'Rep card' : 'Dealer'}</TableCell>
                      <TableCell>{lead.contactName || '—'}</TableCell>
                      <TableCell>
                        {lead.businessName || (lead.leadType === 'rep_card' ? `Rep card — ${lead.salesRep || ''}` : '—')}
                      </TableCell>
                      <TableCell>{lead.salesRep || '—'}</TableCell>
                      <TableCell>
                        {lead.trialDays ? `${lead.trialDays}d` : '—'}
                        {lead.trialScans ? ` / ${lead.trialScans} scans` : ''}
                      </TableCell>
                      <TableCell>{lead.discountPercent ? `${lead.discountPercent}%` : '—'}</TableCell>
                      <TableCell>
                        <SoftTypography variant="button" fontWeight="medium">
                          {lead.clickCount || 0}
                        </SoftTypography>
                        {lead.lastClickedAt ? (
                          <SoftTypography variant="caption" color="secondary" display="block">
                            {new Date(lead.lastClickedAt * 1000).toLocaleDateString()}
                          </SoftTypography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {lead.printedAt ? (
                          <SoftTypography variant="caption">
                            {new Date(lead.printedAt * 1000).toLocaleDateString()}
                          </SoftTypography>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {lead.exportedAt ? (
                          <SoftTypography variant="caption">
                            {new Date(lead.exportedAt * 1000).toLocaleDateString()}
                          </SoftTypography>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {lead.leadType === 'rep_card' ? (
                          <SoftTypography variant="caption">
                            {(lead.signupCount || 0)} signups · {(lead.conversionCount || 0)} conv
                          </SoftTypography>
                        ) : (
                          <Chip
                            size="small"
                            label={lead.status === 'converted' ? 'Converted' : 'New'}
                            color={lead.status === 'converted' ? 'success' : 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <SoftButton size="small" variant="text" color="info" onClick={(e) => copyLink(e, lead)}>
                          Copy link
                        </SoftButton>
                        <SoftButton
                          size="small"
                          variant="text"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); setConfirm({ open: true, lead }); }}
                        >
                          Delete
                        </SoftButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>

      <ImportLeadsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, lead: null })}>
        <DialogTitle>Delete Sales Lead</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete lead for <strong>{confirm.lead?.businessName}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, lead: null })}>Cancel</Button>
          <Button onClick={onDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkConfirm} onClose={() => setBulkConfirm(false)}>
        <DialogTitle>Delete Selected Leads</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {selected.size} selected lead{selected.size === 1 ? '' : 's'}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm(false)}>Cancel</Button>
          <Button onClick={onBulkDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={printPrompt.open} onClose={() => setPrintPrompt({ open: false, leadIds: [] })}>
        <DialogTitle>Labels Printed?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Did the {printPrompt.leadIds.length} label{printPrompt.leadIds.length === 1 ? '' : 's'} print
            successfully? Choosing Yes stamps these leads with today&apos;s date in the Printed column.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintPrompt({ open: false, leadIds: [] })}>No</Button>
          <Button onClick={onMarkPrinted} color="info" variant="contained">Yes, mark printed</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SalesLeadList;
